import { useState, useEffect, useCallback, useRef } from "react";
import { useProfile } from "../../../Components/Hooks/UserHooks";
import { toast } from "react-toastify";
import {
    getCustomerStates,
    createCustomerState,
    updateCustomerState,
    deleteCustomerState
} from "../api/customer-states.api";
import type { CustomerState } from "../types/customer-states.types";
import type { CustomerStateFormData } from "../components/CustomerStateFormModal";

export function useCustomerStates() {
    const { isAdmin } = useProfile();
    const [list, setList] = useState<CustomerState[]>([]);
    const [allStates, setAllStates] = useState<CustomerState[]>([]);
    const [total, setTotal] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [isFetching, setIsFetching] = useState(false);

    const [currentPage, setCurrentPage] = useState(1);
    const perPage = 10;

    const [searchQuery, setSearchQuery] = useState("");
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedState, setSelectedState] = useState<CustomerState | null>(null);
    const [deactivateModalOpen, setDeactivateModalOpen] = useState(false);
    const [stateToDeactivate, setStateToDeactivate] = useState<CustomerState | null>(null);
    const [transitionsModalOpen, setTransitionsModalOpen] = useState(false);
    const [stateForTransitions, setStateForTransitions] = useState<CustomerState | null>(null);

    const searchTimeoutRef = useRef<any>(null);

    // Fetch paginated data
    const fetchStates = useCallback(async (page: number, query: string) => {
        setIsFetching(true);
        try {
            const queryParams: any = {
                page,
                limit: perPage,
            };
            if (query) {
                queryParams.search = query;
            }

            const response = await getCustomerStates(queryParams);

            if (response && 'data' in response && Array.isArray(response.data)) {
                setList(response.data);
                setTotal(response.meta?.total ?? response.data.length);
            } else if (Array.isArray(response)) {
                setList(response);
                setTotal(response.length);
            } else if (response && Array.isArray((response as any).data)) {
                setList((response as any).data);
                setTotal((response as any).meta?.total ?? (response as any).data.length);
            } else {
                setList([]);
                setTotal(0);
            }
        } catch (error) {
            console.error("Error loading customer states:", error);
            toast.error("Error al cargar los estados de cliente");
        } finally {
            setIsFetching(false);
        }
    }, [perPage]);

    // Fetch all states (un-paginated) for transitions configurator
    const fetchAllStates = useCallback(async () => {
        try {
            const response = await getCustomerStates({ limit: 1000 });
            if (response && 'data' in response && Array.isArray(response.data)) {
                setAllStates(response.data);
            } else if (Array.isArray(response)) {
                setAllStates(response);
            } else if (response && Array.isArray((response as any).data)) {
                setAllStates((response as any).data);
            } else {
                setAllStates([]);
            }
        } catch (error) {
            console.error("Error loading all customer states:", error);
        }
    }, []);

    // Initial load of all states
    useEffect(() => {
        fetchAllStates();
    }, [fetchAllStates]);

    // Debounced load on page/search change
    useEffect(() => {
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        searchTimeoutRef.current = setTimeout(() => {
            fetchStates(currentPage, searchQuery);
        }, 300);

        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, [currentPage, searchQuery, fetchStates]);

    const handleSearch = (query: string) => {
        setSearchQuery(query);
        setCurrentPage(1);
    };

    const handlePageChange = (pageIndex: number) => {
        setCurrentPage(pageIndex + 1);
    };

    const handleEdit = (state: CustomerState) => {
        setSelectedState(state);
        setModalOpen(true);
    };

    const handleDeactivate = (state: CustomerState) => {
        setStateToDeactivate(state);
        setDeactivateModalOpen(true);
    };

    const handleConfigureTransitions = (state: CustomerState) => {
        setStateForTransitions(state);
        setTransitionsModalOpen(true);
    };

    const handleDeactivateConfirm = async () => {
        if (!stateToDeactivate) return;
        setIsLoading(true);
        try {
            await deleteCustomerState(stateToDeactivate.id);
            toast.success("Estado de cliente desactivado correctamente");
            setDeactivateModalOpen(false);
            setStateToDeactivate(null);
            fetchStates(currentPage, searchQuery);
            fetchAllStates();
        } catch (error: any) {
            console.error("Error deactivating customer state:", error);
            toast.error(error.message || "Error al desactivar el estado de cliente");
        } finally {
            setIsLoading(false);
        }
    };

    const handleModalSubmit = async (values: CustomerStateFormData) => {
        setIsLoading(true);
        try {
            if (selectedState) {
                await updateCustomerState(selectedState.id, {
                    name: values.name,
                    type: values.type,
                    description: values.description,
                    deactivatePrevious: values.type === 'INITIAL'
                });
                toast.success("Estado de cliente actualizado correctamente");
            } else {
                await createCustomerState({
                    name: values.name,
                    type: values.type,
                    description: values.description,
                    deactivatePrevious: values.type === 'INITIAL'
                });
                toast.success("Estado de cliente creado correctamente");
            }
            setModalOpen(false);
            setSelectedState(null);
            fetchStates(currentPage, searchQuery);
            fetchAllStates();
        } catch (error: any) {
            console.error("Error saving customer state:", error);
            if (error.status === 422) {
                const errorMsg = error.message || "Ya existe un estado inicial. Solo se permite un estado inicial.";
                toast.error(errorMsg);
            } else {
                toast.error(error.message || "Error al guardar el estado de cliente");
            }
        } finally {
            setIsLoading(false);
        }
    };

    const refresh = () => {
        fetchStates(currentPage, searchQuery);
        fetchAllStates();
    };

    return {
        list,
        allStates,
        total,
        isLoading,
        isFetching,
        currentPage,
        searchQuery,
        modalOpen,
        selectedState,
        deactivateModalOpen,
        stateToDeactivate,
        transitionsModalOpen,
        stateForTransitions,
        isAdmin,
        handleSearch,
        handlePageChange,
        handleEdit,
        handleDeactivate,
        handleConfigureTransitions,
        handleDeactivateConfirm,
        handleModalSubmit,
        setModalOpen,
        setSelectedState,
        setDeactivateModalOpen,
        setStateToDeactivate,
        setTransitionsModalOpen,
        setStateForTransitions,
        refresh
    };
}
